import { useMemo, useState } from "react";
import {
  validateFilter,
  type FieldDefinition,
  type FilterCondition,
} from "@estelle/domain";

import { ConditionRow } from "@/components/filters/ConditionRow";
import { Button } from "@/components/ui/button";

/**
 * One condition, edited on a local draft and committed on submit.
 *
 * The popover a chip opens and the popover a freshly-picked field opens are
 * the same shape — `field operator value`, with a way to remove it — so both
 * render this rather than each growing its own copy of `ConditionRow` plus a
 * submit button. `onRemove` means two different things to its two callers: a
 * chip's popover deletes the condition it represents, a not-yet-added one
 * just discards the draft. Neither needs to say so here; `ConditionRow`
 * already renders one remove control and either caller can wire it.
 *
 * A form rather than a bare button: pressing Enter inside the value input is
 * how the keyboard-only path finishes an edit, and a submit button beside a
 * text input does that for free without a keydown handler of its own.
 */
export function ConditionEditor({
  condition,
  fields,
  submitLabel,
  onSubmit,
  onRemove,
}: {
  condition: FilterCondition;
  fields: FieldDefinition[];
  submitLabel: string;
  onSubmit: (condition: FilterCondition) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(condition);
  // "Needs a value" waits for the person to try (#633): said before they had
  // chosen anything, it read as a mistake they had already made.
  const [attempted, setAttempted] = useState(false);

  const problems = useMemo(
    () =>
      validateFilter(
        { kind: "group", combinator: "and", children: [draft] },
        fields,
      ),
    [draft, fields],
  );

  return (
    <form
      className="flex w-80 flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (problems.length === 0) onSubmit(draft);
        else setAttempted(true);
      }}
    >
      <ConditionRow
        condition={draft}
        fields={fields}
        problem={attempted ? problems[0]?.message : undefined}
        onChange={setDraft}
        onRemove={onRemove}
      />
      <div className="flex justify-end border-t pt-2">
        <Button type="submit" size="sm">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
